/**
 * File-level schema deduplication.
 *
 * When several schemas in one file share structurally identical sub-trees
 * (a reused `Address` object, a `{ id, name }` row, an exported schema that is
 * also embedded in another), the error-collecting "slow walk" for that shape
 * is generated ONCE as a shared `__zcSw_N(input, path, issues)` function and
 * every occurrence calls it instead of inlining a fresh copy.
 *
 * Two invariants keep this free of runtime cost and footguns:
 *
 *  1. **Slow walk only.** The fast path (the `&&` boolean chain that validates
 *     valid input with zero allocations) is NEVER shared — it stays fully
 *     inlined so the hot path is byte-for-byte what it was before. Only the
 *     cold, bulky error walk (64–77% of generated bytes) is shared.
 *
 *  2. **No per-export references.** A shared body lives at MODULE scope, while
 *     `__rf[]` — the array of retained Zod schemas and captured callbacks — is
 *     declared inside each export's IIFE. So a shape may be shared only when
 *     nothing in it resolves through `__rf`: no `default`, `catch`, `fallback`,
 *     captured `transform`, or captured `refine`/`superRefine`. Zero-capture
 *     callbacks are fine — they are hosted from source text into the shared
 *     block's own preamble.
 *
 * Sharing used to additionally require the whole ROOT to be mutation-free, so
 * that a body which wrote nothing back could not lose an output value. That
 * excluded a genuine `z.object()` — it strips, so its walk PRODUCES a rebuilt
 * object — which, once `z.object()` began always stripping, meant the feature
 * stopped firing for essentially every real schema: a realistic module of four
 * schemas reusing two sub-shapes shared nothing and shipped 29.6 KB where the
 * `strictObject` spelling of the same thing shipped 16.7 KB.
 *
 * The write-back problem is solved directly instead: a shared walk RETURNS its
 * value and call sites assign it (`slot = __zcSw_N(slot, path, issues)`), so a
 * stripping object, a coercion or a `.trim()` delivers its rewritten value
 * through the call exactly as an inlined walk delivers it through `g.output`.
 */

import type { SchemaIR } from "../types.js";
import type { CodeGenContext, CodegenMode } from "./context.js";
import { createSlowGen, generateSlow } from "./slow-path.js";

/** A shared slow-walk validator hosted at module scope. */
export interface SharedSchemaRef {
  /** Name of the `function __zcSw_N(input, path, issues)` declaration. */
  readonly name: string;
}

export interface SharedSchemaPlan {
  /**
   * Shared ref for an IR node, or undefined when the node is not a shared
   * candidate. Keyed by structural identity (memoized), so repeated lookups
   * during codegen are O(1).
   */
  refFor(ir: SchemaIR): SharedSchemaRef | undefined;
  /** Module-scope declarations (shared preamble + `__zcSw_N` functions). `""` when empty. */
  readonly code: string;
  /** Runtime helper names referenced by the shared block (lean mode). */
  readonly usedHelpers: Set<string>;
  readonly isEmpty: boolean;
}

/**
 * Minimum subtree weight (≈ node count, +1 per check) for a repeated shape to
 * be worth a shared function. Below this the call + dynamic-path overhead
 * outweighs the duplicated inline walk; ~4 admits a 3-field object or an
 * array-of-object while skipping bare primitives and thin wrappers.
 */
const MIN_SHARED_WEIGHT = 4;

/** Leading marker of a shared block; used to skip re-injection on watch/HMR re-runs. */
export const SHARED_BLOCK_MARKER = "/* zod-compiler shared */";

const EMPTY_PLAN: SharedSchemaPlan = {
  refFor: () => undefined,
  code: "",
  usedHelpers: new Set(),
  isEmpty: true,
};

/**
 * Build the shared-schema plan for one file's root schemas. Returns
 * {@link EMPTY_PLAN} (a no-op) when nothing repeats — the common case, so files
 * without internal duplication pay only a single linear analysis pass.
 */
export function createSharedSchemaPlan(
  roots: readonly SchemaIR[],
  mode: CodegenMode,
): SharedSchemaPlan {
  const keyMemo = new Map<SchemaIR, string>();
  const weightMemo = new Map<SchemaIR, number>();
  const stats = new Map<string, { ir: SchemaIR; count: number; weight: number }>();

  for (const root of roots) {
    collect(root, keyMemo, weightMemo, stats);
  }

  const selected = [...stats.values()]
    .filter(
      (c) =>
        c.count >= 2 &&
        c.weight >= MIN_SHARED_WEIGHT &&
        !containsRecursiveRef(c.ir) &&
        !referencesExportRefs(c.ir),
    )
    // Largest shapes first so the biggest wins get the lowest indices; tie-break
    // on the structural key for deterministic, reproducible output.
    .sort((a, b) => b.weight - a.weight || cmp(keyOf(a.ir, keyMemo), keyOf(b.ir, keyMemo)));

  if (selected.length === 0) return EMPTY_PLAN;

  const refs = new Map<string, SharedSchemaRef>();
  selected.forEach((c, i) => refs.set(keyOf(c.ir, keyMemo), { name: `__zcSw_${i}` }));

  const refFor = (ir: SchemaIR): SharedSchemaRef | undefined => refs.get(keyOf(ir, keyMemo));

  const ctx: CodeGenContext = {
    preamble: [],
    counter: 0,
    fnName: "__zcShared",
    regexCache: new Map(),
    mode,
    usedHelpers: new Set(),
  };
  // Generation consults the plan so a shared candidate nested inside another
  // shared candidate becomes a `__zcSw_*` call rather than a second inline copy.
  // Containment is strictly size-decreasing, so the shared functions form a DAG;
  // hoisted declarations make call order irrelevant.
  ctx.sharedSchemas = { refFor, code: "", usedHelpers: ctx.usedHelpers, isEmpty: false };

  const fns: string[] = [];
  for (const c of selected) {
    const ref = refs.get(keyOf(c.ir, keyMemo));
    if (ref === undefined) continue;
    // Generate the candidate's body INLINE (the root is passed to generateSlow,
    // not visit(), so it never replaces itself with a call); only its children
    // route through visit() and may resolve to other shared refs.
    const body = generateSlow(c.ir, createSlowGen("input", "input", "path", "_e", ctx));
    // `input` is both the read and the write slot (createSlowGen above passes it
    // as input AND output), so a walk that rewrites — a stripping object, a
    // coercion, an overwrite check — leaves its result there. Returning it is
    // what lets the caller receive that value; a walk that rewrites nothing
    // returns its argument unchanged.
    fns.push(`function ${ref.name}(input,path,_e){\n${body}\nreturn input;\n}`);
  }

  return {
    refFor,
    code: [SHARED_BLOCK_MARKER, ...ctx.preamble, ...fns].join("\n"),
    usedHelpers: ctx.usedHelpers,
    isEmpty: false,
  };
}

// A shared sub-schema is called as `slot = __zcSw_N(slot, path, issues)`: the
// walk pushes directly into the caller's `issues` array (no result object, no
// per-call issue copy) and returns the parsed value, which the call site assigns
// back into the slot the inlined walk would have written. Call sites are emitted
// by slow-path.ts (nested occurrences) and index.ts (a shared root), keyed off
// `ref.name`.

/**
 * Does the subtree resolve anything through the per-export `__rf[]` array —
 * a retained Zod schema (`default`, `catch`, `fallback`) or a captured callback
 * (`transform`, `refine`, `superRefine`)?
 *
 * Those names exist only inside an export's own IIFE, so a module-scope shared
 * body referencing them would be unbound. A zero-capture callback carries
 * `source` instead of `refIndex` and is hosted into the shared block's preamble,
 * so it does not disqualify a shape.
 */
function referencesExportRefs(ir: SchemaIR): boolean {
  switch (ir.type) {
    case "default":
    case "catch":
    case "fallback":
      // `refIndex` is mandatory on these — the value/schema lives on the export.
      return true;
    case "custom":
      // Both the predicate and pristine error delegate live on the export.
      return true;
    case "zodDelegate":
      return true;
    case "effect":
      if (ir.refIndex !== undefined) return true;
      break;
    case "literal":
      // Only a literal whose value list has no source form (symbol, object)
      // carries a refIndex, and it reads that list off `__rf[N]` on every
      // parse. Sharing is also what would make two DISTINCT symbols collide:
      // `keyOf` serializes an IR field with JSON.stringify, which returns
      // `undefined` for any symbol, so `z.literal(a)` and `z.literal(b)` key
      // identically. The refIndex differs, so the keys differ anyway — but this
      // guard is the one that does not depend on that coincidence.
      if (ir.refIndex !== undefined) return true;
      break;
    case "string":
    case "number":
    case "object":
    case "array":
      // BOTH of a check's `__rf[]` handles disqualify sharing, not just the
      // captured predicate: a `.refine(fn, { params })` reads its params object
      // off the export's array too, and does so even when the predicate itself
      // is zero-capture and would otherwise be hostable into the shared block.
      if (
        ir.checks?.some(
          (c) =>
            ("refIndex" in c && c.refIndex !== undefined) ||
            ("paramsRefIndex" in c && c.paramsRefIndex !== undefined),
        )
      ) {
        return true;
      }
      break;
    default:
      break;
  }
  return childSchemas(ir).some(referencesExportRefs);
}

// ─── Candidate collection ────────────────────────────────────────────────────

function collect(
  ir: SchemaIR,
  keyMemo: Map<SchemaIR, string>,
  weightMemo: Map<SchemaIR, number>,
  stats: Map<string, { ir: SchemaIR; count: number; weight: number }>,
): void {
  const key = keyOf(ir, keyMemo);
  const existing = stats.get(key);
  if (existing) {
    existing.count++;
  } else {
    stats.set(key, { ir, count: 1, weight: weightOf(ir, weightMemo) });
  }
  for (const child of childSchemas(ir)) collect(child, keyMemo, weightMemo, stats);
}

/** Direct child SchemaIR nodes (the structural recursion shared by every pass). */
function childSchemas(ir: SchemaIR): SchemaIR[] {
  switch (ir.type) {
    case "object":
      return Object.values(ir.properties);
    case "array":
      return [ir.element];
    case "tuple":
      return ir.rest === null ? ir.items : [...ir.items, ir.rest];
    case "record":
    case "map":
      return [ir.keyType, ir.valueType];
    case "set":
      return [ir.valueType];
    case "union":
    case "discriminatedUnion":
      return ir.options;
    case "intersection":
      return [ir.left, ir.right];
    case "optional":
    case "nullable":
    case "readonly":
    case "default":
    case "catch":
      return [ir.inner];
    case "pipe":
      return [ir.in, ir.out];
    case "effect":
    case "recursionTarget":
    case "zodDelegate":
      return [ir.inner];
    default:
      return [];
  }
}

/** Subtree weight ≈ node count plus one per check; memoized for O(total) cost. */
function weightOf(ir: SchemaIR, memo: Map<SchemaIR, number>): number {
  const hit = memo.get(ir);
  if (hit !== undefined) return hit;
  const checks = (ir as { checks?: readonly unknown[] }).checks;
  let weight = 1 + (Array.isArray(checks) ? checks.length : 0);
  for (const child of childSchemas(ir)) weight += weightOf(child, memo);
  memo.set(ir, weight);
  return weight;
}

function containsRecursiveRef(ir: SchemaIR): boolean {
  if (ir.type === "recursiveRef") return true;
  return childSchemas(ir).some(containsRecursiveRef);
}

// ─── Structural keys ─────────────────────────────────────────────────────────

/**
 * Stable structural key for an IR node, memoized by node identity so each
 * subtree is serialized exactly once (the whole pass is O(total nodes), not
 * O(nodes × depth)). Two nodes share a key iff they are structurally identical
 * — including every check, literal value, and error message — so deduplicated
 * schemas produce byte-identical validation and issues.
 */
function keyOf(ir: SchemaIR, memo: Map<SchemaIR, string>): string {
  const hit = memo.get(ir);
  if (hit !== undefined) return hit;
  const obj = ir as unknown as Record<string, unknown>;
  const key = `{${Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort()
    .map((k) => `${k}:${encodeValue(obj[k], memo)}`)
    .join(",")}}`;
  memo.set(ir, key);
  return key;
}

/**
 * Encode any IR field value. A nested SchemaIR is detected by a string `type`
 * field (checks carry `kind`, union `cases` carry `value`/`option` — only real
 * schema nodes have `type`) and routed through {@link keyOf} for subtree
 * memoization; everything else is serialized in sorted-key order.
 */
function encodeValue(value: unknown, memo: Map<SchemaIR, string>): string {
  if (typeof value === "bigint") return `#${value}`;
  if (value === null) return "null";
  // Non-finite numbers must stay distinct: JSON.stringify maps Infinity, -Infinity
  // and NaN all to "null", which would merge e.g. .min(Infinity) with .min(NaN).
  if (typeof value === "number" && !Number.isFinite(value)) return `n!${value}`;
  if (Array.isArray(value)) return `[${value.map((v) => encodeValue(v, memo)).join(",")}]`;
  if (typeof value === "object") {
    if (typeof (value as { type?: unknown }).type === "string") {
      return keyOf(value as SchemaIR, memo);
    }
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort()
      .map((k) => `${k}:${encodeValue(obj[k], memo)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "undefined";
}

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
